import React from 'react';
import { Sparkles } from 'lucide-react';

function DigitBoxes({ result, size = 'lg' }) {
  const digits = (result || '---').split('');
  const sizeClass =
    size === 'lg'
      ? 'h-16 w-12 text-3xl sm:h-20 sm:w-16 sm:text-4xl'
      : 'h-12 w-9 text-xl sm:h-14 sm:w-11 sm:text-2xl';

  return (
    <div className="flex justify-center gap-2 sm:gap-3">
      {digits.map((digit, index) => (
        <div
          key={`${digit}-${index}`}
          className={`flex items-center justify-center rounded-xl border-2 border-brand-gold bg-white font-mono font-bold text-brand-red shadow-md ${sizeClass}`}
        >
          {digit}
        </div>
      ))}
    </div>
  );
}

export function YikiLatestResult({
  result,
  roundNumber,
  variant = 'hero',
  className = '',
}) {
  const hasResult = Boolean(result);
  const twoDigit = hasResult ? result.slice(-2) : '--';

  if (variant === 'compact') {
    return (
      <div
        className={`rounded-xl border-2 border-brand-gold/60 bg-gradient-to-br from-brand-gold-light/40 to-white p-3 ${className}`}
      >
        <p className="mb-2 text-center text-[10px] font-semibold uppercase tracking-wider text-brand-gold">
          งวดล่าสุด{roundNumber ? ` #${roundNumber}` : ''}
        </p>
        {hasResult ? (
          <>
            <DigitBoxes result={result} size="sm" />
            <p className="mt-2 text-center text-xs text-gray-500">
              2 ตัวท้าย{' '}
              <span className="font-mono font-bold text-brand-red">{twoDigit}</span>
            </p>
          </>
        ) : (
          <p className="text-center font-mono text-2xl font-bold text-gray-300">
            — — —
          </p>
        )}
      </div>
    );
  }

  return (
    <div
      className={`relative overflow-hidden rounded-2xl border-2 border-brand-gold bg-gradient-to-br from-brand-red via-brand-red to-brand-red-dark p-5 text-white shadow-lg sm:p-6 ${className}`}
    >
      <div className="pointer-events-none absolute -right-6 -top-6 h-24 w-24 rounded-full bg-brand-gold/20 blur-2xl" />
      <div className="pointer-events-none absolute -bottom-8 -left-4 h-20 w-20 rounded-full bg-white/10 blur-xl" />

      <div className="relative flex items-center justify-center gap-2">
        <Sparkles className="h-5 w-5 text-brand-gold" />
        <p className="text-sm font-semibold uppercase tracking-widest text-brand-gold">
          งวดล่าสุด{roundNumber ? ` · รอบ #${roundNumber}` : ''}
        </p>
        <Sparkles className="h-5 w-5 text-brand-gold" />
      </div>

      {hasResult ? (
        <>
          <div className="relative mt-4">
            <DigitBoxes result={result} size="lg" />
          </div>
          <div className="relative mt-4 flex justify-center gap-4 text-sm">
            <span className="rounded-full bg-white/15 px-4 py-1.5 backdrop-blur-sm">
              3 ตัวตรง{' '}
              <span className="font-mono font-bold text-brand-gold">{result}</span>
            </span>
            <span className="rounded-full bg-brand-gold/25 px-4 py-1.5 backdrop-blur-sm">
              2 ตัวท้าย{' '}
              <span className="font-mono font-bold text-white">{twoDigit}</span>
            </span>
          </div>
        </>
      ) : (
        <p className="relative mt-6 text-center text-lg text-red-100">
          ยังไม่มีผลรางวัล
        </p>
      )}
    </div>
  );
}

export function YikiResultHistory({ items, className = '' }) {
  const pastResults = items.slice(1);
  if (pastResults.length === 0) return null;

  return (
    <div className={className}>
      <p className="mb-2 text-xs font-medium text-gray-500">งวดก่อนหน้า</p>
      <div className="flex flex-wrap gap-2">
        {pastResults.map((item) => (
          <div
            key={`${item.roundNumber}-${item.drawAt}`}
            className="rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-1.5 text-center"
          >
            <span className="font-mono text-sm font-semibold text-gray-600">
              {item.result}
            </span>
            <span className="mt-0.5 block text-[10px] text-gray-400">
              #{item.roundNumber}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
